import React from 'react';

/**
 * Représente une partie d'un texte avec son type (ajouté, supprimé, inchangé)
 */
export type TextPart = {
	type: 'added' | 'removed' | 'unchanged';
	text: string;
};

/**
 * Calcule les différences entre deux chaînes de caractères
 * en utilisant une approche basée sur les mots pour identifier
 * les parties ajoutées, supprimées et inchangées
 * 
 * @param oldText - L'ancien texte
 * @param newText - Le nouveau texte
 * @returns Un tableau de parties avec leur type (ajouté, supprimé, inchangé)
 */
export function calculateTextDiff(oldText: string, newText: string): TextPart[] {
	if (!oldText && !newText) {
		return [];
	}

	if (!oldText) {
		return [{ type: 'added', text: newText }];
	}

	if (!newText) {
		return [{ type: 'removed', text: oldText }];
	}

	const oldNormalized = oldText.trim();
	const newNormalized = newText.trim();

	if (oldNormalized === newNormalized) {
		return [{ type: 'unchanged', text: newText }];
	}

	// Diviser en mots en préservant les espaces
	const oldWords = splitPreservingSpaces(oldNormalized);
	const newWords = splitPreservingSpaces(newNormalized);

	// Calculer les différences entre les mots
	const diff = calculateWordDiff(oldWords, newWords);
	
	// Reconstruire le texte avec les types appropriés
	const result: TextPart[] = [];
	
	for (const item of diff) {
		if (item.type === 'unchanged') {
			result.push({ type: 'unchanged', text: item.text });
		} else if (item.type === 'added') {
			result.push({ type: 'added', text: item.text });
		} else if (item.type === 'removed') {
			result.push({ type: 'removed', text: item.text });
		}
	}

	return result;
}

/**
 * Divise une chaîne en mots en préservant les espaces
 */
function splitPreservingSpaces(text: string): Array<{ word: string; isSpace: boolean }> {
	const result: Array<{ word: string; isSpace: boolean }> = [];
	let currentWord = '';
	let isSpace = false;

	for (const char of text) {
		const charIsSpace = /\s/.test(char);
		
		if (charIsSpace !== isSpace && currentWord) {
			result.push({ word: currentWord, isSpace });
			currentWord = '';
		}
		
		isSpace = charIsSpace;
		currentWord += char;
	}
	
	if (currentWord) {
		result.push({ word: currentWord, isSpace });
	}
	
	return result;
}

/**
 * Calcule les différences entre deux tableaux de mots
 */
function calculateWordDiff(
	oldWords: Array<{ word: string; isSpace: boolean }>,
	newWords: Array<{ word: string; isSpace: boolean }>
): Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> {
	const result: Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> = [];
	
	// Utiliser un algorithme simple de correspondance
	let oldIndex = 0;
	let newIndex = 0;

	while (oldIndex < oldWords.length || newIndex < newWords.length) {
		if (oldIndex >= oldWords.length) {
			// Plus de mots dans l'ancien texte, tout le reste est ajouté
			const addedText = newWords.slice(newIndex).map(w => w.word).join('');
			if (addedText.trim()) {
				result.push({ type: 'added', text: addedText });
			}
			break;
		}

		if (newIndex >= newWords.length) {
			// Plus de mots dans le nouveau texte, tout le reste est supprimé
			const removedText = oldWords.slice(oldIndex).map(w => w.word).join('');
			if (removedText.trim()) {
				result.push({ type: 'removed', text: removedText });
			}
			break;
		}

		const oldWord = oldWords[oldIndex];
		const newWord = newWords[newIndex];

		// Comparer les mots (ignorer les espaces pour la comparaison)
		if (oldWord.word.trim() === newWord.word.trim() && oldWord.isSpace === newWord.isSpace) {
			// Mots identiques
			result.push({ type: 'unchanged', text: oldWord.word });
			oldIndex++;
			newIndex++;
		} else {
			// Chercher la prochaine correspondance dans le nouveau texte
			let foundMatch = false;
			for (let i = newIndex + 1; i < newWords.length && i < newIndex + 10; i++) {
				if (oldWord.word.trim() === newWords[i].word.trim() && oldWord.isSpace === newWords[i].isSpace) {
					// Partie ajoutée trouvée
					const addedText = newWords.slice(newIndex, i).map(w => w.word).join('');
					if (addedText.trim()) {
						result.push({ type: 'added', text: addedText });
					}
					newIndex = i;
					foundMatch = true;
					break;
				}
			}

			// Si pas de correspondance, chercher dans l'ancien texte
			if (!foundMatch) {
				for (let i = oldIndex + 1; i < oldWords.length && i < oldIndex + 10; i++) {
					if (newWord.word.trim() === oldWords[i].word.trim() && newWord.isSpace === oldWords[i].isSpace) {
						// Partie supprimée trouvée
						const removedText = oldWords.slice(oldIndex, i).map(w => w.word).join('');
						if (removedText.trim()) {
							result.push({ type: 'removed', text: removedText });
						}
						oldIndex = i;
						foundMatch = true;
						break;
					}
				}
			}

			// Si toujours pas de correspondance, traiter comme modification
			if (!foundMatch) {
				// Si les mots sont très similaires, considérer comme modification
				if (oldWord.word.trim() && newWord.word.trim() && 
					oldWord.word.trim().length > 0 && newWord.word.trim().length > 0) {
					// Comparer caractère par caractère pour identifier les parties ajoutées
					const charDiff = calculateCharDiffForWords(oldWord.word, newWord.word);
					result.push(...charDiff);
				} else {
					// Traiter comme ajout/suppression
					if (oldWord.word.trim()) {
						result.push({ type: 'removed', text: oldWord.word });
					}
					if (newWord.word.trim()) {
						result.push({ type: 'added', text: newWord.word });
					}
				}
				oldIndex++;
				newIndex++;
			}
		}
	}

	return result;
}

/**
 * Calcule les différences caractère par caractère pour des mots
 */
function calculateCharDiffForWords(oldWord: string, newWord: string): Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> {
	const result: Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }> = [];
	
	// Trouver le préfixe commun
	let prefixLength = 0;
	while (prefixLength < oldWord.length && prefixLength < newWord.length && 
		   oldWord[prefixLength] === newWord[prefixLength]) {
		prefixLength++;
	}

	// Trouver le suffixe commun
	let suffixLength = 0;
	while (suffixLength < oldWord.length - prefixLength && 
		   suffixLength < newWord.length - prefixLength &&
		   oldWord[oldWord.length - 1 - suffixLength] === newWord[newWord.length - 1 - suffixLength]) {
		suffixLength++;
	}

	// Ajouter le préfixe inchangé
	if (prefixLength > 0) {
		result.push({ type: 'unchanged', text: oldWord.slice(0, prefixLength) });
	}

	// Ajouter la partie supprimée (si elle existe)
	if (prefixLength < oldWord.length - suffixLength) {
		const removed = oldWord.slice(prefixLength, oldWord.length - suffixLength);
		if (removed) {
			result.push({ type: 'removed', text: removed });
		}
	}

	// Ajouter la partie ajoutée (si elle existe)
	if (prefixLength < newWord.length - suffixLength) {
		const added = newWord.slice(prefixLength, newWord.length - suffixLength);
		if (added) {
			result.push({ type: 'added', text: added });
		}
	}

	// Ajouter le suffixe inchangé
	if (suffixLength > 0) {
		result.push({ type: 'unchanged', text: oldWord.slice(oldWord.length - suffixLength) });
	}

	return result.length > 0 ? result : [{ type: 'unchanged', text: oldWord }];
}

/**
 * Formate les parties de texte pour l'affichage avec les styles appropriés
 * Utilisé pour le bloc "nouveau" (isNewBlock = true)
 */
export function formatTextForDisplay(
	parts: TextPart[],
	isNewBlock: boolean,
	isDeleted: boolean
): React.ReactNode {
	// Si supprimé, afficher avec la couleur rouge dans les deux blocs
	if (isDeleted) {
		return <span style={{ backgroundColor: '#FB0B244C' }}>{parts.map(p => p.text).join('')}</span>;
	}

	if (!isNewBlock) {
		// Pour l'ancien bloc, on affiche seulement les parties 'unchanged' (sans les parties supprimées)
		const filteredParts = parts.filter(part => part.type === 'unchanged');
		return filteredParts.map((part, index) => {
			return <span key={index}>{part.text}</span>;
		});
	}

	// Pour le nouveau bloc, on affiche les parties 'added' et 'unchanged', 
	// et on affiche aussi les parties 'removed' en rouge pour montrer ce qui a été supprimé
	return parts.map((part, index) => {
		if (part.type === 'removed') {
			// Afficher les parties supprimées en rouge dans le bloc nouveau
			return (
				<span key={index} style={{ backgroundColor: '#FB0B244C' }}>
					{part.text}
				</span>
			);
		}
		if (part.type === 'added') {
			// Afficher les parties ajoutées en vert
			return (
				<span key={index} style={{ backgroundColor: '#01C8604C' }}>
					{part.text}
				</span>
			);
		}
		return <span key={index}>{part.text}</span>;
	});
}

